import './styles.scss';

import classNames from 'classnames';
import { isNil } from 'lodash';
import * as moment from 'moment';
import * as React from 'react';
import { InjectedIntlProps, injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { branch, compose, lifecycle, renderComponent } from 'recompose';
import CallToActionBanner, { ICallToActionBannerProps } from 'src/components/CallToActionBanner';
import IMPComponent from 'src/components/interfaces/IMPComponent';
import { DefaultDateFormats } from 'src/constants/dateFormats';
import PATHS from 'src/constants/paths';
import { getSelectedDate } from 'src/containers/PagesHistory/PagesHistoryCalendar/selectors';
import { getPagesHistory, makeGetPagesHistoryExistingPage } from 'src/containers/PagesHistory/selectors';

import StreakInfoComponent from '../../../components/StreakInfo';
import withActionsDispatch from '../../../hocs/withActionsDispatch';
import { getAuthToken } from '../../Auth/selectors';
import { SubscriptionState } from '../../../Models';
import { getSubscriptionsState } from '../../UserProfile/selectors';
import * as pagesHistoryActions from '../actions';
import PagesHistoryCalendar from '../PagesHistoryCalendar';
import * as pagesHistoryHomeActions from './actions';
import CreatePage from './CreatePage';
import PageDetails, { IPageDetailsProps } from './PageDetails';
import styled from 'styled-components';

interface IPagesHistoryHomeProps extends IMPComponent, InjectedIntlProps, IPageDetailsProps {
  readonly isLoggedIn?: boolean;
  readonly hasPages: boolean;
  readonly callToActionBannerProps?: ICallToActionBannerProps;
  readonly selectedDate: string;
  readonly refreshPagesHistory: typeof pagesHistoryActions.refreshPagesHistory;
  readonly subscriptionState: SubscriptionState | null;
}

const enhance = compose<IPagesHistoryHomeProps, IPagesHistoryHomeProps & RouteComponentProps>(
  withRouter,
  injectIntl,
  withActionsDispatch({
    onMount: [
      pagesHistoryHomeActions.startPagesHistoryHomeSaga(),
      pagesHistoryHomeActions.loadPagesHistoryHomeState(),
      pagesHistoryHomeActions.checkPagesHistoryHomeQuoteLastSeenDate(),
    ],
    onUnmount: [pagesHistoryHomeActions.cancelPagesHistoryHomeSaga()],
  }),
  connect(
    state => {
      const date = getSelectedDate(state);
      const pages = getPagesHistory(state);
      return {
        page: makeGetPagesHistoryExistingPage(date)(state),
        hasPages: Object.keys(pages).length > 0,
        selectedDate: moment(date).format(DefaultDateFormats.pageDetails),
        isLoggedIn: Boolean(getAuthToken(state)),
        subscriptionState: getSubscriptionsState(state),
      };
    },
    () => {
      return {
        refreshPagesHistory: pagesHistoryActions.refreshPagesHistory,
      };
    },
  ),
  lifecycle<IPagesHistoryHomeProps, any, any>({
    componentDidMount() {
      const { isLoggedIn, refreshPagesHistory } = this.props;

      if (isLoggedIn) {
        refreshPagesHistory();
      }
    },
  }),
);

const RightContent = branch<IPageDetailsProps>(
  ({ page }) => isNil(page),
  renderComponent(CreatePage),
  renderComponent(PageDetails),
)(() => null);

const Container = styled.div`
  display: flex;
  justify-content: space-between;
  flex-grow: 1;
`;

const LeftContainer = styled.div`
  box-sizing: border-box;
  flex-grow: 1;
  flex-basis: 100%;
  border-right: 1px solid ${p => p.theme.color.lightGrey};
  overflow: auto;
  margin-top: 2.5em;
  margin-bottom: 2.3em;
  padding-right: 5em;
  padding-left: 7em;
`;

const RightContainer = styled.div`
  box-sizing: border-box;
  flex-grow: 1;
  padding: 2.5em 5em;
  padding-right: 7em;
  flex-basis: 100%;
  overflow: auto;
`;

class PagesHistoryHome extends React.Component<IPagesHistoryHomeProps & RouteComponentProps> {
  public static defaultProps = {
    isLoggedIn: false,
  };

  public render() {
    const { style, className, isLoggedIn, selectedDate, page, subscriptionState } = this.props;
    const cn = classNames('PagesHistoryHome', className);

    return (
      <div className={cn} style={style}>
        <Container>
          <LeftContainer>
            <div className="PagesHistoryHome-calendar">
              <PagesHistoryCalendar />
            </div>
            {!isLoggedIn && (
              <div className="PagesHistoryHome-createAccountBanner">
                <CallToActionBanner
                  primaryMessage={this.getCallToActionBannerMessage('primaryMessage')}
                  secondaryMessage={this.getCallToActionBannerMessage('secondaryMessage')}
                  buttonText={this.getCallToActionBannerMessage('buttonText')}
                  buttonProps={{
                    onClick: this.handleCreateAccountButtonClick,
                  }}
                />
              </div>
            )}
            {isLoggedIn && (
              <div className="PagesHistoryHome-streakBanner">
                <StreakInfoComponent />
              </div>
            )}
          </LeftContainer>
          <RightContainer>
            <div className="PagesHistoryHome-selectedDate">{selectedDate}</div>
            <div className="PagesHistoryHome-pageContainer">
              <RightContent page={page} subscriptionState={subscriptionState} />
            </div>
          </RightContainer>
        </Container>
      </div>
    );
  }

  private getCallToActionBannerMessage = (messageId: string) => {
    const { intl } = this.props;
    const id = `containers.PagesHistory.PagesHistoryHome.CallToActionBanner.${messageId}`;

    return intl.formatMessage({
      id,
    });
  };

  private handleCreateAccountButtonClick = () => {
    const { history } = this.props;

    history.push(PATHS.AUTH.SIGN_UP);
  };
}

export default enhance(PagesHistoryHome);



// WEBPACK FOOTER //
// ./src/containers/PagesHistory/PagesHistoryHome/index.tsx